import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, MoreHorizontal, Send, Heart, MessageCircle, ChevronUp, ChevronLeft, ChevronRight, Bookmark } from "lucide-react";

/**
 * StoryPreviewDialog
 * Renders a generated 9:16 ad inside a realistic Instagram-Story phone frame
 * so the user can see what UI Meta overlays on top of their image
 * (story progress bar, profile bubble, "Sponsored", "Mehr erfahren" pill,
 * share + bookmark icons). Helps catch cases where the AI rendered fake
 * platform chrome INTO the image.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open) => void
 *  - imageSrc: data URL or backend URL
 *  - accountName: optional brand name shown in the "Sponsored" header
 *  - ctaText: optional CTA label for the bottom pill (default "Mehr erfahren")
 *  - segments: number of story segments to draw in the top progress bar
 *  - activeSegment: index of the currently-active segment (0-based)
 */
export const StoryPreviewDialog = ({
  open,
  onOpenChange,
  imageSrc,
  carouselSlides,
  accountName = "deinkonto",
  ctaText = "Mehr erfahren",
  segments = 4,
  activeSegment = 1,
}) => {
  const [platform, setPlatform] = useState("instagram"); // 'instagram' | 'facebook' | 'plain'
  const [slideIdx, setSlideIdx] = useState(0);
  const isCarousel = Array.isArray(carouselSlides) && carouselSlides.length > 0;
  const activeSrc = isCarousel
    ? carouselSlides[Math.min(slideIdx, carouselSlides.length - 1)]?.imageSrc
    : imageSrc;

  // Reset slide index when opening / changing source
  useEffect(() => {
    if (open) setSlideIdx(0);
  }, [open, isCarousel ? carouselSlides?.length : null]);

  if (!activeSrc) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 bg-transparent border-none shadow-none max-w-md sm:max-w-md"
        data-testid="story-preview-dialog"
      >
        <div className="flex flex-col items-center gap-3 py-2">
          {/* Platform switch (only single-image story mode) */}
          {!isCarousel && (
            <div className="flex gap-1 bg-black/60 backdrop-blur rounded-full p-1">
              {[
                { id: "instagram", label: "Instagram" },
                { id: "facebook", label: "Facebook" },
                { id: "plain", label: "Original" },
              ].map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPlatform(p.id)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    platform === p.id
                      ? "bg-white text-black"
                      : "text-white/70 hover:text-white"
                  }`}
                  data-testid={`story-preview-platform-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}

          {isCarousel && (
            <div className="text-white/90 text-sm font-medium bg-black/60 backdrop-blur rounded-full px-3 py-1">
              Karussell-Vorschau · Instagram Feed
            </div>
          )}

          {/* Phone frame */}
          <div
            className={`relative w-[300px] ${
              isCarousel ? "aspect-[4/5]" : "aspect-[9/16]"
            } rounded-[28px] bg-black overflow-hidden shadow-2xl ring-4 ring-black/40`}
            data-testid="story-preview-frame"
          >
            {/* The actual ad image — fills the frame */}
            <img
              src={activeSrc}
              alt="Story Preview"
              className="absolute inset-0 w-full h-full object-cover"
            />

            {isCarousel && (
              <>
                {/* Carousel pagination dots */}
                <div className="absolute top-3 left-1/2 -translate-x-1/2 flex gap-1 z-20">
                  {carouselSlides.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 rounded-full transition-all ${
                        i === slideIdx ? "w-6 bg-white" : "w-1.5 bg-white/40"
                      }`}
                    />
                  ))}
                </div>
                {/* Prev / Next buttons */}
                {slideIdx > 0 && (
                  <button
                    onClick={() => setSlideIdx((i) => Math.max(0, i - 1))}
                    className="absolute left-1 top-1/2 -translate-y-1/2 z-20 bg-black/40 backdrop-blur rounded-full p-1.5 text-white"
                    data-testid="story-preview-prev"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                {slideIdx < carouselSlides.length - 1 && (
                  <button
                    onClick={() => setSlideIdx((i) => Math.min(carouselSlides.length - 1, i + 1))}
                    className="absolute right-1 top-1/2 -translate-y-1/2 z-20 bg-black/40 backdrop-blur rounded-full p-1.5 text-white"
                    data-testid="story-preview-next"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
                {/* Slide counter */}
                <div className="absolute top-3 right-3 z-20 bg-black/50 backdrop-blur rounded-full px-2 py-0.5 text-[10px] text-white font-medium">
                  {slideIdx + 1}/{carouselSlides.length}
                </div>
              </>
            )}

            {!isCarousel && platform === "instagram" && (
              <InstagramStoryChrome
                accountName={accountName}
                ctaText={ctaText}
                segments={segments}
                activeSegment={activeSegment}
              />
            )}

            {!isCarousel && platform === "facebook" && (
              <FacebookStoryChrome
                accountName={accountName}
                ctaText={ctaText}
                segments={segments}
                activeSegment={activeSegment}
              />
            )}

            {isCarousel && (
              <>
                {/* Instagram-Feed-Carousel Chrome: top header + bottom CTA */}
                <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/40 to-transparent px-3 py-2 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-500 via-orange-400 to-yellow-400 p-[1.5px]">
                    <div className="w-full h-full rounded-full bg-zinc-700" />
                  </div>
                  <span className="text-white text-[10px] font-semibold drop-shadow">{accountName}</span>
                  <span className="text-white/70 text-[9px]">· Gesponsert</span>
                  <div className="flex-1" />
                  <MoreHorizontal className="w-3.5 h-3.5 text-white/80" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/50 to-transparent px-3 py-2 flex items-center gap-2">
                  <button className="flex-1 bg-white text-black text-[11px] font-semibold rounded-md py-1.5">
                    {ctaText}
                  </button>
                  <Heart className="w-4 h-4 text-white" />
                  <MessageCircle className="w-4 h-4 text-white" />
                  <Send className="w-4 h-4 text-white rotate-12" />
                  <Bookmark className="w-4 h-4 text-white ml-auto" />
                </div>
              </>
            )}
          </div>

          {/* Caption */}
          <p className="text-xs text-white/80 text-center max-w-[280px] px-2">
            {isCarousel
              ? `Karussell mit ${carouselSlides.length} Slides. Klicke ← / → um durchzuswipen.`
              : "Vorschau mit echtem Plattform-Chrome. So sehen deine Follower die Anzeige — graue Bereiche werden überlagert."}
          </p>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => onOpenChange(false)}
            data-testid="story-preview-close"
          >
            Schließen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const InstagramStoryChrome = ({ accountName, ctaText, segments, activeSegment }) => {
  return (
    <>
      {/* Top progress bars */}
      <div className="absolute top-2 left-2 right-2 flex gap-1 z-10">
        {Array.from({ length: segments }).map((_, i) => (
          <div
            key={i}
            className="flex-1 h-[2px] rounded-full bg-white/30 overflow-hidden"
          >
            <div
              className="h-full bg-white"
              style={{
                width:
                  i < activeSegment
                    ? "100%"
                    : i === activeSegment
                    ? "55%"
                    : "0%",
              }}
            />
          </div>
        ))}
      </div>

      {/* Top header: avatar + name + Sponsored */}
      <div className="absolute top-5 left-2 right-2 flex items-center gap-2 z-10">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 via-orange-400 to-yellow-400 p-[2px]">
          <div className="w-full h-full rounded-full bg-zinc-700" />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-white text-[11px] font-semibold drop-shadow">
            {accountName}
          </span>
          <span className="text-white/70 text-[10px] drop-shadow">
            Sponsored
          </span>
        </div>
        <div className="flex-1" />
        <button className="text-white/90 p-1">
          <MoreHorizontal className="w-4 h-4" />
        </button>
        <button className="text-white/90 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Bottom CTA pill */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <ChevronUp className="w-4 h-4 text-white/80 animate-bounce" />
        <button
          className="bg-white text-black text-[12px] font-semibold rounded-full px-5 py-1.5 shadow-md"
          tabIndex={-1}
        >
          {ctaText}
        </button>
      </div>

      {/* Right side icons (reply / share) */}
      <div className="absolute bottom-3 right-3 flex flex-col items-center gap-3 z-10 text-white/90">
        <Send className="w-4 h-4 rotate-12" />
      </div>

      {/* Reply input hint at very bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30 z-10" />
    </>
  );
};

const FacebookStoryChrome = ({ accountName, ctaText, segments, activeSegment }) => {
  return (
    <>
      {/* Top progress bars */}
      <div className="absolute top-2 left-2 right-2 flex gap-1 z-10">
        {Array.from({ length: segments }).map((_, i) => (
          <div
            key={i}
            className="flex-1 h-[2px] rounded-full bg-white/30 overflow-hidden"
          >
            <div
              className="h-full bg-white"
              style={{
                width:
                  i < activeSegment
                    ? "100%"
                    : i === activeSegment
                    ? "55%"
                    : "0%",
              }}
            />
          </div>
        ))}
      </div>

      {/* Top header: avatar + name + Sponsored */}
      <div className="absolute top-5 left-2 right-2 flex items-center gap-2 z-10">
        <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-[11px] font-bold">
          {accountName?.[0]?.toUpperCase() || "F"}
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-white text-[11px] font-semibold drop-shadow">
            {accountName}
          </span>
          <span className="text-white/70 text-[10px] drop-shadow">
            Gesponsert
          </span>
        </div>
        <div className="flex-1" />
        <button className="text-white/90 p-1">
          <MoreHorizontal className="w-4 h-4" />
        </button>
        <button className="text-white/90 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Bottom CTA bar — Facebook uses a wider button */}
      <div className="absolute bottom-3 left-3 right-3 z-10 flex items-center gap-2">
        <button
          className="flex-1 bg-blue-600 text-white text-[12px] font-semibold rounded-md py-2 shadow-md"
          tabIndex={-1}
        >
          {ctaText}
        </button>
        <button className="bg-black/30 backdrop-blur rounded-full p-2 text-white">
          <Heart className="w-4 h-4" />
        </button>
        <button className="bg-black/30 backdrop-blur rounded-full p-2 text-white">
          <MessageCircle className="w-4 h-4" />
        </button>
        <button className="bg-black/30 backdrop-blur rounded-full p-2 text-white">
          <Bookmark className="w-4 h-4" />
        </button>
      </div>
    </>
  );
};

export default StoryPreviewDialog;
